import { useMutation } from '@apollo/client';
import type { DeleteCustomDatasourceMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteCustomDatasourceDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteCustomDatasourceTRPC } from './useDeleteCustomDatasourceTRPC';

export const useDeleteCustomDatasource = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation — mirrors the legacy cache eviction on the two
  // custom_datasource query families.
  const [deleteCustomDatasourceGraphQL, graphqlState] = useMutation(
    DeleteCustomDatasourceDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_datasource');
        evictField(cache, 'custom_datasource_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteCustomDatasourceTRPC();

  const deleteCustomDatasource = async (variables: {
    Id: string;
  }): Promise<DeleteCustomDatasourceMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteCustomDatasource(variables);
    }

    const result = await deleteCustomDatasourceGraphQL({
      variables: { Id: variables.Id },
    });
    if (!result.data) {
      throw new Error('Failed to delete custom datasource');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteCustomDatasource,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteCustomDatasource,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
